import { useState } from 'react';
import {
  ArrowClockwise,
  Copy,
  DotsThreeVertical,
  FileText,
  Hash,
  MagicWand,
  MagnifyingGlass,
  PencilSimple,
  Plus,
  Trash,
  WarningCircle,
  X,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { copyTextToClipboard } from '@/lib/clipboard';
import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { cn } from '@/lib/utils';
import { useDuplicateTemplate } from '../../hooks/use-templates';
import type { TemplateListItem } from '../../types/templates.types';
import { getTemplateErrorMessage } from '../../utils/template-api-errors';
import {
  formatTemplateCategoryLabel,
  formatTemplateUpdatedDate,
  getTemplateContentStatus,
} from '../../utils/template-list.utils';

const TemplatesTableSkeletonBlock = ({ className }: { className?: string }) => (
  <span className={cn('inline-block animate-pulse rounded bg-[#EDEEF0]', className)} aria-hidden />
);

const TEMPLATES_TABLE_SKELETON_ROW_COUNT = 4;

const TemplatesTableSkeletonRows = () => (
  <>
    {Array.from({ length: TEMPLATES_TABLE_SKELETON_ROW_COUNT }, (_, index) => (
      <TableRow
        key={`templates-skeleton-${index}`}
        className="border-b border-[#F4F5F6] hover:bg-transparent"
      >
        <TableCell className="px-4 py-3">
          <TemplatesTableSkeletonBlock className="block h-3.5 w-40" />
        </TableCell>
        <TableCell className="px-4 py-3">
          <TemplatesTableSkeletonBlock className="block h-5 w-20 rounded-full" />
        </TableCell>
        <TableCell className="px-4 py-3">
          <TemplatesTableSkeletonBlock className="block h-3.5 w-[50px]" />
        </TableCell>
        <TableCell className="px-4 py-3">
          <TemplatesTableSkeletonBlock className="block h-3.5 w-[100px]" />
        </TableCell>
        <TableCell className="px-4 py-3" />
      </TableRow>
    ))}
  </>
);

type TemplatesTableProps = {
  templates: TemplateListItem[];
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  hasActiveFilters: boolean;
  onRetry?: () => void;
  onClearFilters?: () => void;
  onCreate?: () => void;
  onDelete: (template: TemplateListItem) => void;
};

export const TemplatesTable = ({
  templates,
  isLoading = false,
  isError = false,
  errorMessage,
  hasActiveFilters,
  onRetry,
  onClearFilters,
  onCreate,
  onDelete,
}: TemplatesTableProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { mutateAsync: duplicateTemplate, isPending: isDuplicating } = useDuplicateTemplate();
  const [duplicatingTemplateId, setDuplicatingTemplateId] = useState<string | null>(null);

  const handleDuplicateTemplate = async (template: TemplateListItem) => {
    if (isDuplicating) return;

    setDuplicatingTemplateId(template.itemId);
    try {
      const duplicate = await duplicateTemplate(template.itemId);
      toast({
        variant: 'success',
        title: 'Template duplicated',
        description: `"${duplicate.name}" was created successfully.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to duplicate template',
        description: getTemplateErrorMessage(error, 'Please try again.'),
      });
    } finally {
      setDuplicatingTemplateId(null);
    }
  };

  const handleCopyId = async (template: TemplateListItem, mountTarget?: Element) => {
    const copied = await copyTextToClipboard(template.itemId, { mountTarget });
    if (copied) {
      toast({ variant: 'success', title: 'Template ID copied' });
    } else {
      toast({ variant: 'destructive', title: 'Could not copy template ID' });
    }
  };

  return (
    <Table className="min-w-[760px] border-collapse" divClassname="min-w-0 overflow-x-auto">
      <TableHeader>
        <TableRow className="border-b border-neutral-200 bg-neutral-50 hover:bg-neutral-50">
          <TableHead className="sticky top-0 z-10 whitespace-nowrap bg-neutral-50 px-4 py-3 text-left text-[13px] font-semibold leading-[18px] text-neutral-600">
            Name
          </TableHead>
          <TableHead className="sticky top-0 z-10 whitespace-nowrap bg-neutral-50 px-4 py-3 text-left text-[13px] font-semibold leading-[18px] text-neutral-600">
            Category
          </TableHead>
          <TableHead className="sticky top-0 z-10 whitespace-nowrap bg-neutral-50 px-4 py-3 text-left text-[13px] font-semibold leading-[18px] text-neutral-600">
            Content
          </TableHead>
          <TableHead className="sticky top-0 z-10 whitespace-nowrap bg-neutral-50 px-4 py-3 text-left text-[13px] font-semibold leading-[18px] text-neutral-600">
            Last updated
          </TableHead>
          <TableHead className="sticky right-0 top-0 z-20 w-12 bg-neutral-50 px-4 py-3" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading ? <TemplatesTableSkeletonRows /> : null}

        {!isLoading && isError ? (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={5} className="px-4 py-14">
              <div className="mx-auto flex max-w-[380px] flex-col items-center gap-2.5 text-center">
                <WarningCircle className="size-7 text-[#D80032]" aria-hidden />
                <p className="text-base font-semibold text-neutral-900">Could not load templates</p>
                <p className="text-sm text-neutral-600">
                  {errorMessage ||
                    'Something went wrong while fetching your template library. Check your connection and try again.'}
                </p>
                {onRetry ? (
                  <Button
                    type="button"
                    className="mt-1 gap-2 bg-primary-600 hover:bg-primary-700"
                    onClick={onRetry}
                  >
                    <ArrowClockwise className="size-4" aria-hidden />
                    Retry
                  </Button>
                ) : null}
              </div>
            </TableCell>
          </TableRow>
        ) : null}

        {!isLoading && !isError && templates.length === 0 ? (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={5} className="px-4 py-14">
              <div className="mx-auto flex max-w-[380px] flex-col items-center gap-2.5 text-center">
                {hasActiveFilters ? (
                  <MagnifyingGlass className="size-7 text-[#707070]" aria-hidden />
                ) : (
                  <FileText className="size-7 text-[#707070]" aria-hidden />
                )}
                <p className="text-base font-semibold text-neutral-900">
                  {hasActiveFilters ? 'No templates match' : 'No templates yet'}
                </p>
                <p className="text-sm text-neutral-600">
                  {hasActiveFilters
                    ? 'Try a different search term, or clear the category filter.'
                    : 'Save a layout once and reuse it across campaigns instead of starting from a blank page every time.'}
                </p>
                <Button
                  type="button"
                  variant={hasActiveFilters ? 'ghost' : 'default'}
                  className={
                    hasActiveFilters
                      ? 'mt-1 gap-2'
                      : 'mt-1 gap-2 bg-primary-600 hover:bg-primary-700'
                  }
                  onClick={hasActiveFilters ? onClearFilters : onCreate}
                >
                  {hasActiveFilters ? (
                    <X className="size-4" aria-hidden />
                  ) : (
                    <Plus className="size-4" aria-hidden />
                  )}
                  {hasActiveFilters ? 'Clear filters' : 'New template'}
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ) : null}

        {!isLoading &&
          !isError &&
          templates.map((template) => {
            const name = template.name.trim() || 'Untitled template';
            const editPath = `/templates/edit/${template.itemId}`;
            const isRowDuplicating = duplicatingTemplateId === template.itemId;

            return (
              <TableRow
                key={template.itemId}
                className="cursor-pointer border-b border-[#F4F5F6] hover:bg-[#FAFAFA]"
                onClick={() => navigate(editPath)}
              >
                <TableCell className="px-4 py-3 align-middle text-sm font-medium text-neutral-900">
                  {name}
                </TableCell>
                <TableCell className="px-4 py-3 align-middle">
                  <span className="inline-flex items-center rounded-full bg-[#F4F5F6] px-2 py-0.5 text-xs text-[#27272C]">
                    {formatTemplateCategoryLabel(template.category)}
                  </span>
                </TableCell>
                <TableCell className="px-4 py-3 align-middle text-sm text-neutral-600">
                  {getTemplateContentStatus(template)}
                </TableCell>
                <TableCell className="px-4 py-3 align-middle text-sm whitespace-nowrap text-neutral-600">
                  {formatTemplateUpdatedDate(template.updatedAt)}
                </TableCell>
                <TableCell
                  className="sticky right-0 z-10 bg-white px-4 py-3 text-center align-middle"
                  onClick={(event) => event.stopPropagation()}
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-neutral-500 hover:text-neutral-800"
                        aria-label={`Actions for ${name}`}
                        disabled={isRowDuplicating}
                      >
                        <DotsThreeVertical className="size-[18px]" weight="bold" aria-hidden />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onSelect={() => navigate(editPath)}>
                        <PencilSimple className="mr-2 size-4" aria-hidden />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={isDuplicating}
                        onSelect={(event) => {
                          event.preventDefault();
                          void handleDuplicateTemplate(template);
                        }}
                      >
                        <Copy className="mr-2 size-4" aria-hidden />
                        {isRowDuplicating ? 'Duplicating…' : 'Duplicate'}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() =>
                          navigate('/campaigns/create-campaign', {
                            state: { presetTemplateId: template.itemId },
                          })
                        }
                      >
                        <MagicWand className="mr-2 size-4" aria-hidden />
                        Use in a campaign
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={(event) => {
                          event.preventDefault();
                          void handleCopyId(template, event.currentTarget as Element);
                        }}
                      >
                        <Hash className="mr-2 size-4" aria-hidden />
                        Copy ID
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-[#D80032] focus:bg-[#FCE4EA] focus:text-[#D80032]"
                        onSelect={() => onDelete(template)}
                      >
                        <Trash className="mr-2 size-4" aria-hidden />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
      </TableBody>
    </Table>
  );
};
